import {
  MIN_SPEED_FOR_BEARING_KMH,
  MIN_SAILABLE_TWA,
  POLAR_CHART_TWA_RANGE,
  POLAR_CHART_TWS_RANGE
} from "constants/WindAnalysisConstants.js";
import DirectionUtil from "utils/DirectionUtil.js";
import type {
  GroupedPoints,
  OptimalVMGAngles,
  PerformanceVsTarget,
  PolarDataPoint,
  TargetSpeedResult
} from "./PolarChartUtil.types.js";

export type {
  GroupedPoints,
  OptimalVMGAngles,
  PerformanceVsTarget,
  PolarDataPoint,
  SpeedTwaPoint,
  TargetSpeedResult
} from "./PolarChartUtil.types.js";

/**
 * Get bearing for a point, recalculating from GPS if necessary
 * @param {Object} point - Current point
 * @param {Object} prevPoint - Previous point (for calculation)
 * @return {number} Bearing in degrees (0-360) or -1 if invalid
 */
const getBearingForPoint = (point: any, prevPoint: any): number => {
  // If point has valid bearing (not 0, not negative, not NaN), use it
  // Note: We exclude 0 because many GPS devices output 0 when bearing is unknown
  if (point.bearing > 0 && !isNaN(point.bearing)) {
    return point.bearing;
  }

  // Otherwise, calculate from GPS coordinates
  if (
    prevPoint &&
    prevPoint.latitude !== undefined &&
    prevPoint.longitude !== undefined &&
    point.latitude !== undefined &&
    point.longitude !== undefined &&
    !isNaN(prevPoint.latitude) &&
    !isNaN(prevPoint.longitude) &&
    !isNaN(point.latitude) &&
    !isNaN(point.longitude)
  ) {
    const calculatedBearing = DirectionUtil.getBearingValue(
      prevPoint.latitude,
      prevPoint.longitude,
      point.latitude,
      point.longitude
    );

    if (calculatedBearing >= 0 && !isNaN(calculatedBearing)) {
      return calculatedBearing;
    }
  }

  return -1; // Invalid
};

/**
 * Calculate True Wind Angle (TWA) for a point
 * TWA is the angle between the boat's course and the true wind direction (0-180°)
 *
 * According to sailing standards (https://www.katamarans.com/wind-speed-calculator-true-apparent/):
 * - True Wind Direction (TWD) = where wind is coming FROM (meteorological convention)
 * - TWA = angle between boat heading and TWD
 *
 * The windAngle parameter represents where the wind is coming FROM (meteorological convention).
 * This matches how windAngle is stored in the activity (from WeatherMapkit API).
 *
 * @param {number} bearing - Boat course/bearing in degrees (0-360)
 * @param {number} windAngle - Wind direction in degrees (0-360), direction wind is coming FROM
 * @return {number} TWA in degrees (0-180)
 */
export const calculateTWA = (bearing: number, windAngle: number): number | null => {
  if (bearing < 0 || windAngle < 0) {
    return null;
  }

  // Normalize angles to 0-360
  const normalizedBearing = ((bearing % 360) + 360) % 360;
  const normalizedWindAngle = ((windAngle % 360) + 360) % 360;

  // Calculate the angle between course and true wind direction
  // TWA is the absolute difference between bearing and wind direction
  let angleDiff = Math.abs(normalizedBearing - normalizedWindAngle);

  // Handle the case where the angle wraps around 360/0
  // TWA should be between 0 and 180 degrees
  if (angleDiff > 180) {
    angleDiff = 360 - angleDiff;
  }

  return angleDiff;
};

/**
 * Group points by True Wind Angle (TWA) ranges
 *
 * @param {Array} points - Array of points with speedInKMH, bearing (or lat/lon for calculation)
 * @param {number} windAngle - Wind direction in degrees
 * @param {number} rangeSize - Size of TWA range in degrees (default: 10)
 * @return {Object} Object with TWA ranges as keys and arrays of {speed, twa} as values
 */
export const groupPointsByTWA = (points: any[], windAngle: number, rangeSize: number = 10): GroupedPoints => {
  if (!points || points.length === 0 || windAngle < 0) {
    return {};
  }

  const grouped: GroupedPoints = {};

  points.forEach((point, index) => {
    // Filter: speed must be above minimum for reliable bearing data
    if (point.speedInKMH > MIN_SPEED_FOR_BEARING_KMH) {
      // Get bearing (from point or calculate from GPS)
      const prevPoint = index > 0 ? points[index - 1] : null;
      const bearing = getBearingForPoint(point, prevPoint);

      if (bearing >= 0) {
        // Use inferred TWA if available (from track geometry), otherwise calculate from bearing/wind
        // Inferred TWA provides smoother, more accurate results by eliminating noise from GPS bearing
        const twa =
          point.inferredTWA !== null && point.inferredTWA !== undefined
            ? point.inferredTWA
            : calculateTWA(bearing, windAngle);

        // Filter: TWA must be in sailable range (exclude no-go zone)
        if (twa !== null && twa >= MIN_SAILABLE_TWA) {
          // Note: Using inferred TWA reduces the impact of "velocity shifts"
          // (apparent wind changes due to speed changes in gusts/lulls).
          // See: https://www.speedandsmarts.com/toolbox/articles2/the-smart-course/smart-basics

          // Round TWA to nearest range (e.g., 0-10, 10-20, etc.)
          const rangeKey = Math.floor(twa / rangeSize) * rangeSize;
          if (!grouped[rangeKey]) {
            grouped[rangeKey] = [];
          }
          // Store both speed and actual TWA to calculate accurate averages later
          grouped[rangeKey].push({speed: point.speedInKMH, twa: twa});
        }
      }
    }
  });

  return grouped;
};

/**
 * Group points by True Wind Angle (TWA) ranges and tack (port/starboard)
 *
 * @param {Array} points - Array of points with speedInKMH, bearing (or lat/lon for calculation)
 * @param {number} windAngle - Wind direction in degrees
 * @param {Function} getTack - Function to determine tack (port/starboard)
 * @param {number} rangeSize - Size of TWA range in degrees (default: 10)
 * @return {Object} Object with structure {port: {twa: [{speed, twa}]}, starboard: {twa: [{speed, twa}]}}
 */
export const groupPointsByTWAAndTack = (
  points: any[],
  windAngle: number,
  getTack: (bearing: number, windAngle: number) => string,
  rangeSize: number = 10
): {port: GroupedPoints; starboard: GroupedPoints} => {
  if (!points || points.length === 0 || windAngle < 0) {
    return {port: {}, starboard: {}};
  }

  const grouped: {port: GroupedPoints; starboard: GroupedPoints} = {port: {}, starboard: {}};

  points.forEach((point, index) => {
    // Filter: speed must be above minimum for reliable bearing data
    if (point.speedInKMH > MIN_SPEED_FOR_BEARING_KMH) {
      // Get bearing (from point or calculate from GPS)
      const prevPoint = index > 0 ? points[index - 1] : null;
      const bearing = getBearingForPoint(point, prevPoint);

      if (bearing >= 0) {
        // Use inferred TWA if available (from track geometry), otherwise calculate from bearing/wind
        const twa =
          point.inferredTWA !== null && point.inferredTWA !== undefined
            ? point.inferredTWA
            : calculateTWA(bearing, windAngle);

        // Filter: TWA must be in sailable range (exclude no-go zone)
        if (twa !== null && twa >= MIN_SAILABLE_TWA) {
          // Note: Using inferred TWA reduces the impact of velocity shifts
          // See: https://www.speedandsmarts.com/toolbox/articles2/the-smart-course/smart-basics

          const tack = getTack(bearing, windAngle);
          if (tack === "port" || tack === "starboard") {
            // Round TWA to nearest range (e.g., 0-10, 10-20, etc.)
            const rangeKey = Math.floor(twa / rangeSize) * rangeSize;
            if (!grouped[tack][rangeKey]) {
              grouped[tack][rangeKey] = [];
            }
            // Store both speed and actual TWA to calculate accurate averages later
            grouped[tack][rangeKey].push({speed: point.speedInKMH, twa: twa});
          }
        }
      }
    }
  });

  return grouped;
};

/**
 * Calculate polar data from grouped points
 * Returns average and max speed for each TWA range, plus VMG calculations
 *
 * @param {Object} groupedPoints - Object with TWA ranges as keys and arrays of {speed, twa} as values
 * @return {Array} Array of {twa, actualTWA, avgSpeed, maxSpeed, pointCount, avgVMG, vmgEfficiency}
 */
export const calculatePolarData = (groupedPoints: GroupedPoints): PolarDataPoint[] => {
  const polarData: PolarDataPoint[] = [];

  // Sort TWA ranges
  const sortedRanges = Object.keys(groupedPoints)
    .map(Number)
    .sort((a, b) => a - b);

  sortedRanges.forEach((rangeKey) => {
    const dataPoints = groupedPoints[rangeKey];
    if (dataPoints && dataPoints.length > 0) {
      // Calculate actual average TWA from all points in this bucket (not just the bucket boundary)
      // This gives much more accurate optimal angle suggestions
      const avgTWA = dataPoints.reduce((sum, dp) => sum + dp.twa, 0) / dataPoints.length;
      const avgSpeed = dataPoints.reduce((sum, dp) => sum + dp.speed, 0) / dataPoints.length;
      const maxSpeed = Math.max(...dataPoints.map((dp) => dp.speed));

      // Calculate VMG (Velocity Made Good) = speed * cos(TWA)
      // VMG represents progress directly toward/away from wind
      // Use the actual average TWA for more accurate VMG calculation
      const avgVMG = avgSpeed * Math.cos((avgTWA * Math.PI) / 180);
      const maxVMG = maxSpeed * Math.cos((avgTWA * Math.PI) / 180);

      // VMG efficiency: ratio of VMG to speed (0-1, where 1 = sailing directly up/downwind)
      const vmgEfficiency = Math.abs(avgVMG / avgSpeed);

      polarData.push({
        twa: rangeKey, // Keep bucket boundary for chart positioning (0, 10, 20, etc.)
        actualTWA: Math.round(avgTWA), // Store actual average TWA for optimal angle calculations
        avgSpeed: avgSpeed,
        maxSpeed: maxSpeed,
        avgVMG: avgVMG,
        maxVMG: maxVMG,
        vmgEfficiency: vmgEfficiency,
        pointCount: dataPoints.length
      });
    }
  });

  return polarData;
};

/**
 * Find optimal VMG angles from polar data
 * According to sailing tactics:
 * - Upwind optimal: typically 40-50° TWA
 * - Downwind optimal: typically 135-155° TWA (broad reach)
 *
 * @param {Array} polarData - Array of polar data points with VMG calculations
 * @return {Object} {upwindOptimal, downwindOptimal, upwindTargetTWA, downwindTargetTWA}
 */
export const findOptimalVMGAngles = (polarData: PolarDataPoint[]): OptimalVMGAngles => {
  if (!polarData || polarData.length === 0) {
    return {upwindOptimal: null, downwindOptimal: null};
  }

  // Upwind: TWA < 90° - find angle with best VMG upwind (negative VMG, moving toward wind)
  const upwindData = polarData.filter((p) => p.twa < 90 && p.twa >= MIN_SAILABLE_TWA);
  let upwindOptimal: PolarDataPoint | null = null;
  let bestUpwindVMG = 0;

  upwindData.forEach((data) => {
    // For upwind, we want most negative VMG (best progress toward wind)
    if (Math.abs(data.avgVMG) > bestUpwindVMG) {
      bestUpwindVMG = Math.abs(data.avgVMG);
      upwindOptimal = data;
    }
  });

  // Downwind: TWA >= 90° - find angle with best VMG downwind (positive VMG, moving away from wind)
  const downwindData = polarData.filter((p) => p.twa >= 90);
  let downwindOptimal: PolarDataPoint | null = null;
  let bestDownwindVMG = 0;

  downwindData.forEach((data) => {
    if (Math.abs(data.avgVMG) > bestDownwindVMG) {
      bestDownwindVMG = Math.abs(data.avgVMG);
      downwindOptimal = data;
    }
  });

  return {
    upwindOptimal: upwindOptimal,
    downwindOptimal: downwindOptimal,
    // Use actualTWA for suggested angles (more accurate), fall back to bucket twa if not available
    upwindTargetTWA: upwindOptimal ? upwindOptimal.actualTWA || upwindOptimal.twa : null,
    downwindTargetTWA: downwindOptimal ? downwindOptimal.actualTWA || downwindOptimal.twa : null
  };
};

/**
 * Calculate polar data from a single activity
 *
 * @param {Array} points - Array of points
 * @param {number} windAngle - Wind direction in degrees
 * @param {number} rangeSize - Size of TWA range in degrees
 * @return {Array} Array of polar data points
 */
export const calculatePolarFromActivity = (
  points: any[],
  windAngle: number,
  rangeSize: number = POLAR_CHART_TWA_RANGE
): PolarDataPoint[] => {
  const grouped = groupPointsByTWA(points, windAngle, rangeSize);
  return calculatePolarData(grouped);
};

/**
 * Calculate polar data from a single activity, separated by tack.
 *
 * Also returns a "combined" polar (port + starboard merged) derived from the
 * same single iteration through the points. Callers that need a combined
 * polar (e.g. for optimal VMG angle search) should reuse this instead of
 * iterating the points a second time.
 *
 * @param {Array} points - Array of points
 * @param {number} windAngle - Wind direction in degrees
 * @param {Function} getTack - Function to determine tack (port/starboard)
 * @param {number} rangeSize - Size of TWA range in degrees
 * @return {Object} Object with {port: [...], starboard: [...], combined: [...]} polar data arrays
 */
export const calculatePolarFromActivityByTack = (
  points: any[],
  windAngle: number,
  getTack: (bearing: number, windAngle: number) => string,
  rangeSize: number = POLAR_CHART_TWA_RANGE
): {port: PolarDataPoint[]; starboard: PolarDataPoint[]; combined: PolarDataPoint[]} => {
  const grouped = groupPointsByTWAAndTack(points, windAngle, getTack, rangeSize);

  // Merge port + starboard groupings into a combined map so optimal-VMG analysis
  // can reuse the same pass through `points` instead of running calculateTWA /
  // getBearingForPoint a second time over the whole activity.
  const combinedGrouped: GroupedPoints = {};
  Object.keys(grouped.port).forEach((twaKey) => {
    combinedGrouped[twaKey] = grouped.port[twaKey].slice();
  });
  Object.keys(grouped.starboard).forEach((twaKey) => {
    if (!combinedGrouped[twaKey]) {
      combinedGrouped[twaKey] = [];
    }
    combinedGrouped[twaKey].push(...grouped.starboard[twaKey]);
  });

  return {
    port: calculatePolarData(grouped.port),
    starboard: calculatePolarData(grouped.starboard),
    combined: calculatePolarData(combinedGrouped)
  };
};

/**
 * Aggregate polar data from multiple activities
 * Combines data from multiple activities to create a more accurate polar chart
 *
 * @param {Array} activities - Array of activity objects with points and windAngle
 * @param {number} rangeSize - Size of TWA range in degrees
 * @return {Array} Array of aggregated polar data points
 */
export const aggregatePolarFromActivities = (
  activities: any[],
  rangeSize: number = POLAR_CHART_TWA_RANGE
): PolarDataPoint[] => {
  if (!activities || activities.length === 0) {
    return [];
  }

  // Combine all points from all activities, grouped by TWA
  const allGroupedPoints: GroupedPoints = {};

  activities.forEach((activity) => {
    if (activity.points && activity.points.length > 0 && activity.windAngle >= 0) {
      const grouped = groupPointsByTWA(activity.points, activity.windAngle, rangeSize);

      // Merge into allGroupedPoints - now merges {speed, twa} objects
      Object.keys(grouped).forEach((twaKey) => {
        if (!allGroupedPoints[twaKey]) {
          allGroupedPoints[twaKey] = [];
        }
        allGroupedPoints[twaKey].push(...grouped[twaKey]);
      });
    }
  });

  return calculatePolarData(allGroupedPoints);
};

/**
 * Group points by True Wind Speed (TWS) ranges
 * This is used to create multiple polar curves for different wind speeds
 *
 * @param {Array} points - Array of points
 * @param {number} windSpeed - Wind speed in km/h
 * @param {number} rangeSize - Size of TWS range in km/h
 * @return {Object} Object with TWS ranges as keys
 */
export const groupPointsByTWS = (
  points: any[],
  windSpeed: number,
  rangeSize: number = POLAR_CHART_TWS_RANGE
): Record<string, any[]> => {
  if (!points || points.length === 0 || windSpeed < 0) {
    return {};
  }

  const rangeKey = Math.floor(windSpeed / rangeSize) * rangeSize;
  return {[rangeKey]: points};
};

/**
 * Calculate polar data grouped by both TWA and TWS
 * Creates multiple curves for different wind speeds
 *
 * @param {Array} activities - Array of activity objects
 * @param {number} twaRangeSize - Size of TWA range in degrees
 * @param {number} twsRangeSize - Size of TWS range in km/h
 * @return {Object} Object with TWS ranges as keys and polar data arrays as values
 */
export const calculatePolarByWindSpeed = (
  activities: any[],
  twaRangeSize: number = POLAR_CHART_TWA_RANGE,
  twsRangeSize: number = POLAR_CHART_TWS_RANGE
): Record<string, PolarDataPoint[]> => {
  if (!activities || activities.length === 0) {
    return {};
  }

  // Group activities by wind speed
  const activitiesByWindSpeed: Record<string, any[]> = {};

  activities.forEach((activity) => {
    if (activity.points && activity.points.length > 0 && activity.windAngle >= 0 && activity.windSpeedInKM) {
      const twsRange = Math.floor(activity.windSpeedInKM / twsRangeSize) * twsRangeSize;
      if (!activitiesByWindSpeed[twsRange]) {
        activitiesByWindSpeed[twsRange] = [];
      }
      activitiesByWindSpeed[twsRange].push(activity);
    }
  });

  // Calculate polar data for each wind speed range
  const polarByWindSpeed: Record<string, PolarDataPoint[]> = {};

  Object.keys(activitiesByWindSpeed).forEach((twsRange) => {
    const activitiesForTWS = activitiesByWindSpeed[twsRange];
    polarByWindSpeed[twsRange] = aggregatePolarFromActivities(activitiesForTWS, twaRangeSize);
  });

  return polarByWindSpeed;
};

/**
 * Get target speed for a given TWA from polar data
 * Uses linear interpolation between data points (like ORC polars)
 *
 * This is the key function for ORC-style performance targets:
 * Given a TWA (e.g., 45°), it returns what your boat speed SHOULD be
 * based on historical performance data.
 *
 * @param {Array} polarData - Array of polar data points {twa, avgSpeed, maxSpeed}
 * @param {number} targetTWA - The TWA to get target speed for
 * @param {string} speedType - 'avg' or 'max' - which speed curve to use
 * @return {Object} {targetSpeed, targetVMG, confidence} or null
 */
export const getTargetSpeedForTWA = (
  polarData: PolarDataPoint[],
  targetTWA: number,
  speedType: string = "avg"
): TargetSpeedResult | null => {
  if (!polarData || polarData.length === 0 || targetTWA < 0 || targetTWA > 180) {
    return null;
  }

  // Sort polar data by TWA
  const sortedData = [...polarData].sort((a, b) => a.twa - b.twa);

  // Find the two data points that bracket the target TWA
  let lowerPoint: PolarDataPoint | null = null;
  let upperPoint: PolarDataPoint | null = null;

  for (let i = 0; i < sortedData.length; i++) {
    if (sortedData[i].twa <= targetTWA) {
      lowerPoint = sortedData[i];
    }
    if (sortedData[i].twa >= targetTWA && !upperPoint) {
      upperPoint = sortedData[i];
      break;
    }
  }

  // If we have exact match, return it
  if (lowerPoint && lowerPoint.twa === targetTWA) {
    const speed = speedType === "max" ? lowerPoint.maxSpeed : lowerPoint.avgSpeed;
    const vmg = speed * Math.cos((targetTWA * Math.PI) / 180);
    return {
      targetSpeed: speed,
      targetVMG: Math.abs(vmg),
      confidence: lowerPoint.pointCount >= 10 ? "high" : lowerPoint.pointCount >= 5 ? "medium" : "low",
      dataPoints: lowerPoint.pointCount
    };
  }

  // If we have both lower and upper points, interpolate
  if (lowerPoint && upperPoint) {
    const lowerSpeed = speedType === "max" ? lowerPoint.maxSpeed : lowerPoint.avgSpeed;
    const upperSpeed = speedType === "max" ? upperPoint.maxSpeed : upperPoint.avgSpeed;

    // Linear interpolation
    const ratio = (targetTWA - lowerPoint.twa) / (upperPoint.twa - lowerPoint.twa);
    const interpolatedSpeed = lowerSpeed + ratio * (upperSpeed - lowerSpeed);
    const vmg = interpolatedSpeed * Math.cos((targetTWA * Math.PI) / 180);

    // Confidence based on data density
    // For single leg/run analysis, lower thresholds are more appropriate
    // since points are spread across TWA buckets (10° ranges)
    // With 200-400 points per leg across ~10 TWA buckets, 10+ points per bucket indicates good data
    const avgDataPoints = (lowerPoint.pointCount + upperPoint.pointCount) / 2;
    const confidence = avgDataPoints >= 10 ? "high" : avgDataPoints >= 5 ? "medium" : "low";

    return {
      targetSpeed: interpolatedSpeed,
      targetVMG: Math.abs(vmg),
      confidence: confidence,
      dataPoints: Math.round(avgDataPoints)
    };
  }

  // If we only have lower point (extrapolate)
  if (lowerPoint) {
    const speed = speedType === "max" ? lowerPoint.maxSpeed : lowerPoint.avgSpeed;
    const vmg = speed * Math.cos((targetTWA * Math.PI) / 180);
    return {
      targetSpeed: speed,
      targetVMG: Math.abs(vmg),
      confidence: "low", // Extrapolation is less reliable
      dataPoints: lowerPoint.pointCount,
      extrapolated: true
    };
  }

  // If we only have upper point (extrapolate)
  if (upperPoint) {
    const speed = speedType === "max" ? upperPoint.maxSpeed : upperPoint.avgSpeed;
    const vmg = speed * Math.cos((targetTWA * Math.PI) / 180);
    return {
      targetSpeed: speed,
      targetVMG: Math.abs(vmg),
      confidence: "low", // Extrapolation is less reliable
      dataPoints: upperPoint.pointCount,
      extrapolated: true
    };
  }

  return null;
};

/**
 * Calculate performance vs target for a segment
 * Returns percentage of target speed achieved
 *
 * @param {number} actualMetric - Actual average metric for the segment (speed or VMG)
 * @param {number} actualTWA - Actual average TWA for the segment
 * @param {Array} polarData - Polar data for target calculation
 * @param {string} speedType - 'avg' or 'max' target
 * @param {number} segmentPointCount - Optional: total points in the segment (leg/run) for overall confidence
 * @param {Object} optimalAngles - Optional: {upwindTargetTWA, downwindTargetTWA} for TWA optimality check
 * @param {boolean} useVMG - Optional: if true, compare against targetVMG instead of targetSpeed
 * @return {Object} {percentOfTarget, targetSpeed, delta, performance, twaOptimality} or null
 */
export const calculatePerformanceVsTarget = (
  actualMetric: number,
  actualTWA: number,
  polarData: PolarDataPoint[],
  speedType: string = "avg",
  segmentPointCount: number | null = null,
  optimalAngles: OptimalVMGAngles | null = null,
  useVMG: boolean = false
): PerformanceVsTarget | null => {
  if (!actualMetric || !actualTWA || !polarData) {
    return null;
  }

  const target = getTargetSpeedForTWA(polarData, actualTWA, speedType);
  if (!target || !target.targetSpeed) {
    return null;
  }

  // If useVMG is true, compare against targetVMG instead of targetSpeed
  const targetValue = useVMG ? target.targetVMG : target.targetSpeed;
  const percentOfTarget = (actualMetric / targetValue) * 100;
  const delta = actualMetric - targetValue;

  // Check TWA optimality (how far from optimal VMG angle)
  let twaOptimality: string | null = null;
  let twaDeviation: number | null = null;
  if (optimalAngles) {
    const isUpwind = actualTWA < 90;
    const optimalTWA = isUpwind ? optimalAngles.upwindTargetTWA : optimalAngles.downwindTargetTWA;

    if (optimalTWA != null) {
      twaDeviation = Math.abs(actualTWA - optimalTWA);

      // Calculate optimality rating
      // For upwind: optimal is typically 40-50°, acceptable range ±10°
      // For downwind: optimal is typically 135-155°, acceptable range ±15°
      const acceptableRange = isUpwind ? 10 : 15;
      const goodRange = acceptableRange / 2;

      if (twaDeviation <= goodRange) {
        twaOptimality = "optimal"; // Within ±5° (upwind) or ±7.5° (downwind)
      } else if (twaDeviation <= acceptableRange) {
        twaOptimality = "acceptable"; // Within ±10° (upwind) or ±15° (downwind)
      } else if (twaDeviation <= acceptableRange * 2) {
        twaOptimality = "suboptimal"; // Within ±20° (upwind) or ±30° (downwind)
      } else {
        twaOptimality = "poor"; // More than ±20° (upwind) or ±30° (downwind) from optimal
      }
    }
  }

  // Performance rating - consider both speed and TWA optimality
  let performance = "at-target";

  // If TWA is significantly off optimal, downgrade performance even if speed is good
  if (twaOptimality === "poor") {
    // More than 20° from optimal - can't be excellent
    performance = percentOfTarget >= 100 ? "at-target" : percentOfTarget >= 90 ? "below-target" : "poor";
  } else if (twaOptimality === "suboptimal") {
    // 10-20° from optimal - can be above-target at best
    if (percentOfTarget >= 105) {
      performance = "above-target"; // Good speed but not optimal angle
    } else if (percentOfTarget >= 100) {
      performance = "at-target";
    } else if (percentOfTarget >= 90) {
      performance = "below-target";
    } else {
      performance = "poor";
    }
  } else {
    // TWA is optimal or acceptable - use speed-based rating
    if (percentOfTarget >= 105) {
      performance = "excellent"; // 5% above target
    } else if (percentOfTarget >= 100) {
      performance = "above-target";
    } else if (percentOfTarget >= 90) {
      performance = "below-target";
    } else {
      performance = "poor"; // More than 10% below target
    }
  }

  // Calculate overall confidence considering both bucket density and total segment size
  let confidence = target.confidence; // Start with bucket-level confidence

  // If we know the total segment size, factor that in
  // A leg/run with 100+ points should have high confidence regardless of bucket density
  // This addresses the case where points are spread across many TWA buckets
  if (segmentPointCount !== null && segmentPointCount !== undefined) {
    if (segmentPointCount >= 100) {
      confidence = "high"; // Lots of data overall
    } else if (segmentPointCount >= 50) {
      // Boost confidence by one level if it's not already high
      if (confidence === "low") {
        confidence = "medium";
      } else if (confidence === "medium") {
        confidence = "high";
      }
    } else if (segmentPointCount >= 25) {
      // Boost low to medium
      if (confidence === "low") {
        confidence = "medium";
      }
    }
    // If segmentPointCount < 25, keep the bucket-level confidence
  }

  return {
    percentOfTarget: percentOfTarget,
    targetSpeed: target.targetSpeed,
    targetVMG: target.targetVMG,
    delta: delta,
    performance: performance,
    confidence: confidence,
    dataPoints: target.dataPoints,
    segmentPoints: segmentPointCount,
    twaOptimality: twaOptimality,
    twaDeviation: twaDeviation
  };
};
